import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatErr } from "../lib/api";
import { useAuth } from "../lib/auth";
import Navbar from "../components/Navbar";
import Disclaimer from "../components/Disclaimer";
import { toast } from "sonner";
import { ShieldCheck, Zap, FileText, Lock, Sparkles } from "lucide-react";

export default function AutorizzaFatture() {
  const { user, refresh } = useAuth();
  const nav = useNavigate();
  const [loading, setLoading] = useState(false);

  const start = async () => {
    // Preview mode: A-Cube not yet contracted, don't allow real activation
    toast.info("Funzione in arrivo — stiamo perfezionando l'integrazione ufficiale con il provider di delega. Per ora usa l'import CSV/XML dalla pagina Movimenti.");
  };

  const startPreview = async () => {
    // Legacy start for internal demo/preview
    setLoading(true);
    try {
      const r = await api.post("/delega/start", { return_url: `${window.location.origin}/delega/callback` });
      nav(r.data.redirect_url);
    } catch (ex) {
      toast.error(formatErr(ex.response?.data?.detail));
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F9F8F6]">
      <Navbar />
      <div className="max-w-4xl mx-auto px-6 py-12">
        <div className="mb-6 flex items-start gap-3 bg-[#FFF3CD] border border-[#F5D982] rounded-md px-4 py-3" data-testid="coming-soon-banner">
          <Sparkles className="text-[#C25934] mt-0.5" size={18}/>
          <div className="text-sm text-[#664D03]">
            <strong>Presto disponibile:</strong> stiamo finalizzando la partnership con un provider accreditato AgID per attivare la delega automatica. Nel frattempo puoi importare le fatture da CSV o XML SDI dalla pagina Movimenti — è veloce e non richiede setup.
          </div>
        </div>

        <div className="label-caps">In arrivo</div>
        <h1 className="font-serif-display text-4xl lg:text-5xl text-[#1E3F33] mt-3">
          Collega IVA Radar al tuo Cassetto Fiscale
        </h1>
        <p className="mt-4 text-[#2C2C2C] text-lg max-w-2xl leading-relaxed">
          Autorizza IVA Radar ad accedere alle tue fatture direttamente dal Cassetto Fiscale dell'Agenzia delle Entrate. Basta un click con SPID, poi non dovrai fare più nulla: le tue fatture verranno scaricate e aggiornate automaticamente.
        </p>

        <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4 stagger">
          {[
            { icon: Zap, title: "Un solo click", desc: "Accesso con SPID come già fai per Agenzia Entrate o INPS." },
            { icon: FileText, title: "Fatture automatiche", desc: "Non serve più caricare CSV o XML: le fatture arrivano da sole." },
            { icon: ShieldCheck, title: "Connessione sicura", desc: "Delega via provider accreditato AgID. Puoi revocarla in ogni momento." },
          ].map((f, i) => (
            <div key={i} className="card-flat p-5">
              <f.icon className="text-[#C25934]" size={22}/>
              <h3 className="mt-3 font-serif-display text-xl text-[#1E3F33]">{f.title}</h3>
              <p className="mt-1 text-sm text-[#6B6B6B]">{f.desc}</p>
            </div>
          ))}
        </div>

        <div className="mt-10 card-flat p-8 bg-white">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-full bg-[#EBE5DF] grid place-items-center shrink-0">
              <Lock className="text-[#1E3F33]" size={22}/>
            </div>
            <div className="flex-grow">
              <h2 className="font-serif-display text-2xl text-[#1E3F33]">Autorizzazione via SPID</h2>
              <p className="mt-2 text-sm text-[#2C2C2C]">
                Cliccando qui sotto verrai reindirizzato alla schermata di accesso SPID: quella stessa che già usi per Agenzia delle Entrate, INPS o Fascicolo Sanitario. IVA Radar non vede né conserva la tua password SPID.
              </p>
              <button data-testid="autorizza-cta" onClick={start} disabled={true}
                className="btn-lift mt-6 inline-flex items-center gap-2 px-6 py-3 bg-[#6C757D] text-white rounded-md cursor-not-allowed opacity-60">
                <Sparkles size={16}/> Entra con SPID (in arrivo)
              </button>
              <p className="mt-3 text-xs text-[#6B6B6B]">
                <a href="#" data-testid="autorizza-preview-link" onClick={(e)=>{e.preventDefault(); startPreview();}} className="underline hover:text-[#1E3F33]">Vuoi vedere un'anteprima</a> del flusso di attivazione? {loading && "…"}
              </p>
            </div>
          </div>
        </div>

        <div className="mt-8">
          <Disclaimer compact />
        </div>

        <details className="mt-6 text-sm text-[#6B6B6B]" open>
          <summary className="cursor-pointer text-[#1E3F33]">Nel frattempo: importa fatture da CSV o XML SDI</summary>
          <div className="mt-3 card-flat p-4">
            <p className="text-sm">Puoi già usare tutte le funzionalità di IVA Radar caricando le tue fatture manualmente da CSV o file XML della Fattura Elettronica SDI. Il calcolo IVA e gli alert soglia funzionano subito.</p>
            <button data-testid="manual-import-fallback" onClick={()=>nav("/movimenti")} className="mt-3 px-4 py-2 bg-[#1E3F33] text-white rounded-md text-sm hover:bg-[#152D24]">
              Vai a Movimenti
            </button>
          </div>
        </details>
      </div>
    </div>
  );
}
